import Color from 'color'
import * as THREE from 'three'

import { type FrameSettings } from './settings'

// Wall size in meters
export const wallWidth = 10
export const wallHeight = 10

const textureCanvasSize = 1024

// Colors
const defaultBackgroundColor = '#ffffff'
const darkDotColor = '#000000'
const lightDotColor = '#ffffff'

// Alpha
const dotAlphaMin = 0.2
const dotAlphaMax = 0.4

// Radius
const dotRadiusMin = 0.05
const dotRadiusMax = 0.07

/**
 * Gets background color if it's valid, otherwise uses default color.
 */
export const getBackgroundColor = (backgroundColor?: string) => {
  try {
    if (!backgroundColor || !Color(backgroundColor)) {
      throw new Error(`Invalid color "${backgroundColor}"`)
    }

    return backgroundColor
  } catch (error) {
    console.error(error)

    return defaultBackgroundColor
  }
}

/**
 * Generates a frame preview wall texture.
 */
export const getWallTexture = (
  frameSettings: FrameSettings,
  backgroundColor?: string,
) => {
  const maxDimension = Math.max(
    frameSettings.artworkWidth,
    frameSettings.artworkHeight,
  )
  const activeBackgroundColor = getBackgroundColor(backgroundColor)
  const isBackgroundColorLight = Color(activeBackgroundColor).isLight()
  const dotColor = isBackgroundColorLight ? darkDotColor : lightDotColor

  // Create texture using canvas
  const wallTextureCanvas = document.createElement('canvas')
  wallTextureCanvas.width = textureCanvasSize
  wallTextureCanvas.height = textureCanvasSize
  const wallTextureContext = wallTextureCanvas.getContext('2d')

  if (!wallTextureContext) {
    return null
  }

  // Fill background color
  wallTextureContext.globalAlpha = 1.0
  wallTextureContext.fillStyle = activeBackgroundColor
  wallTextureContext.fillRect(
    0,
    0,
    wallTextureCanvas.width,
    wallTextureCanvas.height,
  )

  // Add dots in a regular pattern
  wallTextureContext.beginPath()
  const cellCountX = 1
  const cellCountY = 1
  // Increase radius for bigger frames
  const dotRadius =
    Math.min(
      dotRadiusMax,
      dotRadiusMin + maxDimension * (dotRadiusMax - dotRadiusMin),
    ) * wallTextureCanvas.width

  // Draw dots for specified amount of cells only
  for (let indexX = 0; indexX < cellCountX + 1; indexX += 1) {
    for (let indexY = 0; indexY < cellCountY + 1; indexY += 1) {
      const dotX = (wallTextureCanvas.width * indexX) / cellCountX
      const dotY = (wallTextureCanvas.height * indexY) / cellCountY
      wallTextureContext.moveTo(dotX + dotRadius, dotY)
      wallTextureContext.arc(dotX, dotY, dotRadius, 0, 2 * Math.PI)
    }
  }

  // Increase alpha for bigger frames
  wallTextureContext.globalAlpha = Math.min(
    dotAlphaMax,
    dotAlphaMin + maxDimension * (dotAlphaMax - dotAlphaMin),
  )
  wallTextureContext.fillStyle = dotColor
  wallTextureContext.fill()

  // Repeat the drawn dots - make sure each grid cell is 1cm in size
  const wallTexture = new THREE.CanvasTexture(wallTextureCanvas)
  wallTexture.wrapS = THREE.RepeatWrapping
  wallTexture.wrapT = THREE.RepeatWrapping
  // Convert wall size from m to cm
  wallTexture.repeat.set(
    (wallWidth * 100) / cellCountX,
    (wallHeight * 100) / cellCountY,
  )

  return wallTexture
}
